/**
 * Spec Phase 4 Slice 4C — SpecPage.
 *
 * Top-level workbench for Stage 6. Mirrors DrawingPage's shape:
 *   - Hydrate on mount via useSpecStore.hydrate(matterId)
 *   - Viewport guard via shared <ViewportTooNarrow />
 *   - Branch on hydrationStatus: idle/loading → spinner;
 *     locked → redirect-toast + Back; error → error panel; unlocked
 *     → workbench (3-column layout via <SpecBody />)
 *
 * Real workbench content (sections / paragraphs / docks) lands in
 * Slices 4D + 4E; this slice ships the structural shell + lock
 * handling + seed flow.
 *
 * Per brief §1 work item 1, on entering with drawings unfrozen the
 * route surfaces a toast and a Back button rather than redirecting
 * automatically — the route runner is App.js's view-state, not
 * react-router, so navigation has to be cooperative.
 */
import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Loader2, Lock } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import {
  VIEWPORT_MIN_WIDTH_PX,
  ViewportTooNarrow,
  useViewportGate,
} from '@/components/shared/ViewportTooNarrow';
import { useSpecStore } from '@/store/specStore';
import SpecHeader from './SpecHeader';
import SpecBody from './SpecBody';
import SectionNav from './SectionNav';
import PaperEditor from './PaperEditor';
import DockRail from './DockRail';
import DrawingPopout from './DrawingPopout';
import GenerateEverythingProgress from './GenerateEverythingProgress';
import ShortcutCheatsheet from './ShortcutCheatsheet';
import { useSpecKeyboardShortcuts } from './useSpecKeyboardShortcuts';
import { readDdAcceptedNotice } from './ddAcceptedBroadcast';
import { postEvent } from '@/api/client';
// SEC-F02 (Phase 28 3-I) — defang raw backend error strings
// before rendering them in the error panel.
import { sanitizeApiError } from '@/lib/sanitizeApiError';


/** Phase 12G a11y — render the spec store's toastMessage with
 * ``role="status"`` + ``aria-live="polite"`` so screen-reader users
 * hear two-tab refresh notifications, save errors, and other
 * showToast() messages without focus changes. Mirrors Drawing's
 * ToastSnackbar shape. */
function SpecToast(): JSX.Element | null {
  const message = useSpecStore((s) => s.toastMessage);
  const showToast = useSpecStore((s) => s.showToast);
  if (!message) return null;
  return (
    <div
      className="fixed bottom-16 right-4 z-50 flex max-w-md items-start gap-2 rounded-md border border-emerald-300 bg-white px-3 py-2 shadow-md"
      role="status"
      aria-live="polite"
      data-testid="spec-toast"
    >
      <p className="flex-1 text-xs text-zinc-700">{message}</p>
      <button
        type="button"
        className="text-[11px] font-medium text-zinc-500 hover:text-zinc-900"
        onClick={() => showToast(null)}
      >
        Dismiss
      </button>
    </div>
  );
}


interface Props {
  matterId: string;
  /** Cooperative navigation back to the matter workflow. */
  onBack?: () => void;
  /** Cooperative navigation back to the drawings workbench. Used by
   * the locked-state CTA (drawings must freeze before spec opens). */
  onOpenDrawings?: () => void;
  /** Cooperative navigation to /admin/spec/settings. Admin-only. */
  onOpenSettings?: () => void;
  /** Cooperative navigation to the Description workbench (DD-4). */
  onOpenDescription?: () => void;
  /** The user-guide videos (the Courses link). */
  onOpenCourses?: () => void;
  /** Frozen-claims count from the matter state — surfaced in the
   * subtitle. Optional; defaults to undefined. */
  frozenClaimsCount?: number;
  /** Frozen-drawings count surfaced in the subtitle. */
  frozenDrawingsCount?: number;
  /** BUG-036 — caller-resolved auth role. Threaded into SpecHeader
   * so the CostIndicator only mounts for admins (instead of
   * relying on the indicator's own /auth/me probe). */
  userRole?: string | null;
}

export default function SpecPage({
  matterId,
  onBack,
  onOpenDrawings,
  onOpenSettings,
  onOpenDescription,
  onOpenCourses,
  frozenClaimsCount,
  frozenDrawingsCount,
  userRole,
}: Props): JSX.Element {
  const {
    hydrationStatus,
    lockedReason,
    errorMessage,
    hydratedMatterId,
    specDocument,
    sectionSummary,
    hydrate,
    resetForMatter,
  } = useSpecStore(useShallow((s) => ({
    hydrationStatus: s.hydrationStatus,
    lockedReason: s.lockedReason,
    errorMessage: s.errorMessage,
    hydratedMatterId: s.hydratedMatterId,
    specDocument: s.specDocument,
    sectionSummary: s.sectionSummary,
    hydrate: s.hydrate,
    resetForMatter: s.resetForMatter,
  })));

  // BUG-001 — PostHog session_recording is now disabled at init
  // (see public/index.html). The previous per-component
  // stopSessionRecording-on-mount + startSessionRecording-on-
  // unmount dance was insufficient: PostHog re-attached its
  // MutationObserver on any state update, racing the stop call
  // across re-renders. The init-level disable is the only
  // deterministic kill switch.
  //
  // The stop call below stays as belt-and-braces against a
  // PostHog remote-config override flipping recording back on
  // at runtime. The matching startSessionRecording on unmount
  // was REMOVED — restarting recording on every SpecPage unmount
  // was actively re-introducing the 3-5 minute compositor block
  // when the drafter navigated back to Spec.
  useEffect(() => {
    const ph = (typeof window !== 'undefined'
      ? (window as unknown as {
          posthog?: { stopSessionRecording?: () => void };
        }).posthog
      : undefined);
    try {
      ph?.stopSessionRecording?.();
    } catch {
      // Never let an analytics SDK throw a Spec-page mount.
    }
  }, []);

  // ── Hydrate on mount + matter switch ────────────────────────────
  useEffect(() => {
    if (hydratedMatterId !== matterId) {
      resetForMatter(matterId);
      void hydrate(matterId);
      // Slice 6F — load any persisted offline queue from IDB on
      // matter open. If entries exist, the banner surfaces and a
      // drain attempt fires.
      void useSpecStore.getState().hydrateOfflineQueue();
      // Hydrate the recent generate-everything runs list and
      // resume polling if a job is still running server-side.
      // Fire-and-forget — failure is benign, history is informational.
      void useSpecStore.getState().hydrateGenerateEverythingRuns();
    } else {
      // Same-matter remount (in-tab nav Spec → Claims → Spec).
      // The full ``hydrate`` is intentionally skipped on this
      // path (it'd refetch 6 endpoints worth of unaffected
      // state). Refetch ONLY the slice that a background GE
      // could have changed while the page was unmounted —
      // sections + section_summary — so the rail and the
      // "N empty" header counter reflect any newly-generated
      // paragraphs. Without this the store keeps the pre-GE
      // state from the first visit and the drafter sees stale
      // "empty" badges on sections that actually have content.
      void useSpecStore.getState().refreshSectionsForActiveMatter();
      // Also resume GE-runs polling in case the previous mount's
      // poll terminated and a fresh job was started in another
      // tab. Idempotent — won't duplicate an already-running
      // poll for the same job_id.
      void useSpecStore.getState().hydrateGenerateEverythingRuns();
    }
  }, [matterId, hydratedMatterId, hydrate, resetForMatter]);

  // ── Slice 6F — online-event drain + 30s polling ────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const drain = () => { void useSpecStore.getState().flushOfflineQueue(); };
    window.addEventListener('online', drain);
    const interval = window.setInterval(drain, 30_000);
    return () => {
      window.removeEventListener('online', drain);
      window.clearInterval(interval);
    };
  }, []);

  // ── Phase 12E.1 + 12E.3 — two-tab last-write-wins + numeral cache
  //   invalidation on window focus. Refetches the active section
  //   detail + reference_signs + drawingSummaries; if the active
  //   section's server-side updated_at is newer than the cached
  //   one, fires the toast + replaces the local cache (cursor
  //   position is acceptably lost).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onFocus = () => {
      void useSpecStore.getState().refreshOnFocus();
    };
    // The Description Workbench tab announces an Accept through
    // localStorage; the storage event reaches every other same-origin
    // tab at once, so the paragraphs show here without waiting for
    // the drafter to click back into this window.
    const onStorage = (ev: StorageEvent) => {
      const notice = readDdAcceptedNotice(ev);
      if (!notice || notice.matterId !== matterId) return;
      void useSpecStore.getState().refreshOnFocus();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
    };
  }, [matterId]);

  // SEC-F02 (Phase 28 3-I) — surface the raw errorMessage to the
  // developer console in dev-mode only so the leak-collapse at
  // the render site doesn't hide the failure cause during local
  // debugging. Production builds drop the effect body via the
  // NODE_ENV branch.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && errorMessage) {
      // eslint-disable-next-line no-console
      console.error('[SpecPage] raw errorMessage:', errorMessage);
    }
  }, [errorMessage]);

  // ── spec.page.opened event ──────────────────────────────────────
  // Fires once per mount per matter. PERF-F04 (Phase 28 3-J) — the
  // prior implementation included ``specDocument`` and
  // ``sectionSummary`` in the deps array. Both are object
  // references that change identity on every Zustand ``set()``
  // call (e.g. after a paragraph save updates
  // ``overall_completeness_pct``), so the beacon fired
  // repeatedly throughout a session — inflating analytics counts
  // + producing a misleading "page opens" rollup.
  //
  // Fix: useRef tracks first-fire per mount. The beacon
  // captures the snapshot of specDocument + sectionSummary at
  // first-unlock time; subsequent state changes don't re-fire.
  // A fresh mount (matter switch / navigation away + back)
  // resets the ref so the new mount fires once.
  const pageOpenedFiredRef = useRef(false);
  useEffect(() => {
    if (pageOpenedFiredRef.current) return;
    if (hydrationStatus !== 'unlocked') return;
    if (!matterId) return;
    pageOpenedFiredRef.current = true;
    void postEvent({
      event_type: 'spec.page.opened',
      event_category: 'navigation',
      matter_id: matterId,
      element_id: 'spec_page',
      event_data: {
        jurisdiction: specDocument?.jurisdiction ?? null,
        sections_count: sectionSummary?.total ?? 0,
        drafted_count: sectionSummary?.drafted ?? 0,
      },
    });
  }, [hydrationStatus, matterId, specDocument, sectionSummary]);

  // ── Phase 11 Slice 11E — keyboard shortcuts + cheatsheet ───────
  const [cheatsheetOpen, setCheatsheetOpen] = React.useState(false);
  useSpecKeyboardShortcuts({
    onOpenCheatsheet: () => setCheatsheetOpen(true),
  });

  // ── Viewport guard (shared with Drawing module) ─────────────────
  const viewportTooNarrow = useViewportGate(VIEWPORT_MIN_WIDTH_PX);

  const Header = (
    <SpecHeader
      matterId={matterId}
      specDocument={specDocument}
      sectionSummary={sectionSummary}
      frozenClaimsCount={frozenClaimsCount}
      frozenDrawingsCount={frozenDrawingsCount}
      onBack={onBack}
      onOpenSettings={onOpenSettings}
      onOpenDescription={onOpenDescription}
      onOpenCourses={onOpenCourses}
      userRole={userRole}
    />
  );

  if (viewportTooNarrow) {
    return (
      <ViewportTooNarrow
        moduleName="Specification workbench"
        detail={
          'The section nav, paper editor, and reference rail need at least ' +
          `${VIEWPORT_MIN_WIDTH_PX}px of horizontal space. Open this matter on a ` +
          'desktop to start drafting the specification.'
        }
        header={Header}
        testId="spec-viewport-stub"
      />
    );
  }

  // ── Loading / idle state ───────────────────────────────────────
  if (hydrationStatus === 'idle' || hydrationStatus === 'loading') {
    return (
      <div className="flex min-h-screen flex-col bg-zinc-50">
        {Header}
        <div
          className="flex flex-1 items-center justify-center"
          data-testid="spec-loading"
        >
          <div className="flex items-center gap-3 text-sm text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading specification…
          </div>
        </div>
      </div>
    );
  }

  // ── Locked state — drawings not frozen ─────────────────────────
  if (hydrationStatus === 'locked') {
    return (
      <div className="flex min-h-screen flex-col bg-zinc-50">
        {Header}
        <div
          className="mx-auto flex max-w-xl flex-1 flex-col items-center justify-center px-6 py-16 text-center"
          data-testid="spec-locked"
        >
          <Lock className="mb-4 h-10 w-10 text-zinc-400" aria-hidden />
          <h2 className="mb-2 text-base font-semibold text-zinc-900">
            Specification unlocks when drawings freeze
          </h2>
          <p className="mb-6 text-sm text-zinc-600">
            {lockedReason === 'drawings_not_frozen'
              ? 'At least one drawing must be frozen at Stage 5 before the specification workbench opens.'
              : `Locked: ${lockedReason ?? 'unknown reason'}.`}
          </p>
          <div className="flex items-center gap-3">
            {onOpenDrawings && (
              <button
                type="button"
                className="inline-flex h-9 items-center rounded-sm bg-zinc-900 px-3 text-sm font-medium text-white hover:bg-zinc-800"
                onClick={onOpenDrawings}
                data-testid="spec-locked-open-drawings"
              >
                Open Drawings
              </button>
            )}
            {onBack && (
              <button
                type="button"
                className="inline-flex h-9 items-center rounded-sm border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                onClick={onBack}
                data-testid="spec-locked-back"
              >
                Back to matter
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────
  if (hydrationStatus === 'error') {
    return (
      <div className="flex min-h-screen flex-col bg-zinc-50">
        {Header}
        <div
          className="mx-auto flex max-w-xl flex-1 flex-col items-center justify-center px-6 py-16 text-center"
          data-testid="spec-error"
        >
          <AlertTriangle className="mb-4 h-10 w-10 text-amber-500" aria-hidden />
          <h2 className="mb-2 text-base font-semibold text-zinc-900">
            Failed to load the specification
          </h2>
          {/* SEC-F02 (Phase 28 3-I) — sanitise. Backend 500 ``detail``
              strings can carry internal paths, SQL fragments, or
              LLM provider error bodies; this collapses anything
              with a path / stack-frame marker / long length to a
              generic message. The dev-mode console.error below
              keeps the raw value visible for developers. */}
          <p className="mb-6 text-sm text-zinc-600">
            {sanitizeApiError(errorMessage ?? undefined)}
          </p>
          {onBack && (
            <button
              type="button"
              className="inline-flex h-9 items-center rounded-sm bg-zinc-900 px-3 text-sm font-medium text-white hover:bg-zinc-800"
              onClick={onBack}
              data-testid="spec-error-back"
            >
              Back to matter
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Unlocked → render the workbench shell ──────────────────────
  return (
    <div
      className="flex h-screen flex-col bg-zinc-50"
      data-testid="spec-workbench"
    >
      {Header}
      <SpecBody
        left={<SectionNav />}
        center={<PaperEditor />}
        right={<DockRail />}
      />
      {/* Phase 9 Slice 9C — single-instance drawing pop-out viewer.
          Renders only when popoutFigureId is set. */}
      <DrawingPopout />
      {/* Phase 11 Slice 11D — Generate Everything progress / failures
          banner. Self-renders nothing when no job is active. */}
      <GenerateEverythingProgress />
      {/* Phase 11 Slice 11E — keyboard shortcut cheatsheet. */}
      {cheatsheetOpen && (
        <ShortcutCheatsheet onClose={() => setCheatsheetOpen(false)} />
      )}
      {/* Phase 12G a11y — toast renderer with role=status +
          aria-live=polite. Spec module previously set toastMessage
          but never rendered it; this surface closes the gap. */}
      <SpecToast />
    </div>
  );
}
